// SPDX-FileCopyrightText: 2026 Igor Brkic <igor@hyperglitch.com>
//
// SPDX-License-Identifier: GPL-3.0-or-later

/**
 * S-expression parser (spec §6.1).
 *
 * Unknown nodes are preserved verbatim — the tree is generic and carries
 * anything the file contains, which is what keeps the tool working against
 * future KiCad versions instead of silently dropping data.
 */

import { scan, SexpError } from './tokenizer.ts';
import { type Atom, type Node } from './types.ts';

export function parse(text: string, fileName = '<input>'): Node {
  const roots = parseAll(text, fileName);
  if (roots.length === 0) {
    throw new SexpError('empty file: no s-expression found', { offset: 0, line: 1, column: 1 }, fileName);
  }
  if (roots.length > 1) {
    throw new SexpError(
      `expected a single top-level expression, found ${roots.length}`,
      { offset: 0, line: 1, column: 1 },
      fileName,
    );
  }
  return roots[0]!;
}

/**
 * Parse zero or more top-level expressions.
 *
 * Driven straight off the scanner with an explicit stack rather than recursion
 * over a token array: a real 42 MB board is about 8 million tokens, and
 * materialising them all before building the tree roughly doubles both the
 * allocation count and the parse time. Deep nesting also can't blow the JS
 * stack this way.
 */
export function parseAll(text: string, fileName = '<input>'): Node[] {
  const roots: Node[] = [];
  const stack: Node[] = [];
  /** Whether the node at the same depth has consumed its leading element yet. */
  const consumedFirst: boolean[] = [];
  /** Position of each still-open `(`, for the unclosed-paren message. */
  const openPositions: Array<{ offset: number; line: number; column: number }> = [];

  scan(text, fileName, (kind, value, quoted, offset, line, column) => {
    if (kind === 'open') {
      stack.push({ kind: 'node', name: '', children: [] });
      consumedFirst.push(false);
      openPositions.push({ offset, line, column });
      return;
    }

    if (kind === 'close') {
      const node = stack.pop();
      consumedFirst.pop();
      openPositions.pop();
      if (node === undefined) {
        throw new SexpError('unexpected ")"', { offset, line, column }, fileName);
      }
      const parent = stack[stack.length - 1];
      if (parent === undefined) {
        roots.push(node);
      } else {
        parent.children.push(node);
        consumedFirst[stack.length - 1] = true;
      }
      return;
    }

    const depth = stack.length - 1;
    const node = stack[depth];
    if (node === undefined) {
      throw new SexpError(
        `unexpected token "${value}" outside of any expression`,
        { offset, line, column },
        fileName,
      );
    }

    // A leading *bare* atom becomes the node name and is not repeated in
    // children; a leading quoted string is an ordinary child.
    if (!consumedFirst[depth] && kind === 'atom') {
      node.name = value;
      consumedFirst[depth] = true;
      return;
    }
    consumedFirst[depth] = true;
    const atom: Atom = { kind: 'atom', value, quoted };
    node.children.push(atom);
  });

  if (stack.length > 0) {
    throw new SexpError('unexpected end of file: unclosed "("', openPositions[0]!, fileName);
  }

  return roots;
}
